import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {DbActiveModal} from "../../modules/modal/modal-ref";
import {Website} from "../website/website";
import {
  BOOSTER_FEATURES,
  CONNECTED_FEATURES,
  IMAGE_OPTIMIZER_FEATURES,
  MULTISITE_FEATURES,
  WEBSITE_FEATURES
} from "./features";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {Store} from "@ngrx/store";
import * as fromStore from '../../store';
import {filter, first} from "rxjs/operators";
import {UserSubscriptionType} from "../../user/user-subscription";
import {WebsiteService} from "../website.service";
import {getUserLocalStorage} from "../../store";
import {UserService} from "../../user/user.service";
import {Router} from "@angular/router";

@Component({
    selector: 'db-remove-website',
    templateUrl: './remove-website.component.html',
    styleUrls: ['./remove-website.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RemoveWebsiteComponent implements OnInit {

  @Input() website: Website;

  public features = [];
  public isFreePlan = false;
  public isSubSite = false;
  public loading = false;
  public formSubmitted = false;
  public isMobile: boolean = window.innerWidth <= 991;
  public removeWebsiteForm = new FormGroup({
    password: new FormControl('', [Validators.required]),
  });
  private _userLocalStorage: any;
  public connectedFrom: string = '';

  constructor(
    private readonly _activeModal: DbActiveModal,
    private readonly _router: Router,
    private readonly _store: Store<fromStore.DbStoreState>,
    private readonly _websiteService: WebsiteService,
    private readonly _userService: UserService,
    private readonly _cdr: ChangeDetectorRef,
  ) {
  }

  ngOnInit() {
    this._activeModal.resize(null, 700);
    this.isSubSite = this.website.isNetwork && this.website.parentId;
    this._setConnectedFrom();
    this._getUserSubscription();
    this._getLocalStorage();
    this._setFeatures();
  }

  private _setFeatures(): void {
    this.features = WEBSITE_FEATURES;
    if (this.website.isNetwork && !this.website.parentId) {
      this.features = MULTISITE_FEATURES;
    }
    if (!this.website.domain_hash && this.connectedFrom !== 'speed_optimizer') {
      this.features = CONNECTED_FEATURES;
    }
    if (!this.website.domain_hash && this.connectedFrom === 'speed_optimizer') {
      this.features = BOOSTER_FEATURES;
    }
    if (!this.website.domain_hash && this.connectedFrom === 'image_optimizer') {
      this.features = IMAGE_OPTIMIZER_FEATURES;
    }
  }

  private _getLocalStorage(): void {
    this._store.select(getUserLocalStorage)
      .pipe(
        filter(userLocalStorage => !!userLocalStorage),
        first()
      )
      .subscribe(userLocalStorage => {
        this._userLocalStorage = userLocalStorage;
      });
  }

  private _changeLocalStorageData(): void {
    this._userLocalStorage['onboardingFinished'] = false;
    this._userService.setToLocal(this._userLocalStorage)
      .pipe(
        first(response => !!response),
      )
      .subscribe(response => {
        if (response.success) {
          this._store.dispatch(new fromStore.LoadUserLocalStorage());
        }
      });
  }

  private _getUserSubscription(): void {
    this._store.select(fromStore.getUserSubscription)
      .pipe(
        first(subscription => !!subscription)
      )
      .subscribe(subscription => {
        this.isFreePlan = subscription.type === UserSubscriptionType.FREE;
        this._setValidators();
        this._cdr.markForCheck();
      });
  }

  private _setValidators(): void {
    const validators = [Validators.required];
    if (this.isFreePlan || this.isSubSite) {
      validators.push(Validators.pattern(/(?:^|\W)^REMOVE$(?:$|\W)/));
    }
    this.removeWebsiteForm.get('password').setValidators(validators);
  }

  public close(): void {
    this._activeModal.close();
  }

  public remove(): void {
    if (this.removeWebsiteForm.invalid) return;

    this.formSubmitted = true;
    this.loading = true;
    this._activeModal.hideCloseBtn(true);
    this._activeModal.setBackdrop('static');

    if (this.website.isNetwork && this.website.parentId) {
      this._removeSubSite();
    } else {
      this._removeWebsite();
    }

  }

  private _removeWebsite(): void {
    this._websiteService.removeWebsite(this.website.id, this.removeWebsiteForm.value.password)
      .pipe(
        first(response => !!response, null)
      )
      .subscribe(response => {
        if (response.success) {
          this._changeLocalStorageData();
          this._store.dispatch(new fromStore.RemoveWebsite(this.website.id));
          if (this.website.siblingId && this.website.siblingId.dev) {
            this._store.dispatch(new fromStore.RemoveWebsite(this.website.siblingId.dev));
          }
          this._store.dispatch(new fromStore.LoadUserSubscription());
          this.close();
          this._router.navigate(['/websites']);
        }
        else {
          const msg = response.serverErrors && response.serverErrors.message;
          this.removeWebsiteForm.get('password').setErrors({server: msg});
          this.loading = false;
          this._activeModal.hideCloseBtn(false);
          this._activeModal.setBackdrop(true);
          this._cdr.markForCheck();
        }
      });
  }

  private _removeSubSite(): void {
    this._websiteService.removeSubDomain(this.website.parentId, this.website.id)
      .pipe(
        first(response => !!response, null)
      )
      .subscribe(response => {
        if (response.success) {
          this._router.navigate(['/websites/' + this.website?.parentId + (this.website?.isNetwork ? '/my-sites' : '/main')]);
          this._changeLocalStorageData();
          this._store.dispatch(new fromStore.RemoveWebsite(this.website?.id));
          this._store.dispatch(new fromStore.LoadUserSubscription());
          this.close();
        }
        else {
          this.loading = false;
          this._activeModal.hideCloseBtn(false);
          this._activeModal.setBackdrop(true);
          this._cdr.markForCheck();
        }
      });
  }

  private _setConnectedFrom(): void {
    this.connectedFrom = this.website.connectedFrom;
    if (this.connectedFrom === 'speed_optimizer' && this.website.managerVersion.includes('iowd')) {
      this.connectedFrom = 'image_optimizer';
    }
  }
}
